import { GroupMember } from '../../grouping';

/** The key every person's share of an item is kept under. */
export const SHARE_KEY = 'share';

/** What the people table says while it is empty, and so why save is disabled. */
export const NO_PEOPLE = 'Add at least one person';

/** A figure of a person's own beside their share, such as their income after tax. */
export type AmountField = {
  key: string;
  /** The column heading, which also names the field for each person it is filled in for. */
  label: string;
};

export type Allocation = {
  clientId: string;
  amounts: Record<string, number | ''>;
};

/** The share column every form has: the share, then what it comes to of the item's amount. */
type ShareColumn = {
  /** The heading for what a share comes to, named as the item's amount is. */
  amountLabel: string;
  /** The item's amount a share is a part of; null until it is entered. */
  of: number | null;
  /** The share a blank one stands for while no share is recorded; null once one is. */
  assumed: number | null;
};

export type AllocationConfig = {
  members: GroupMember[];
  allocations: Allocation[];
  share: ShareColumn;
  /** Money each person has of their own, after the share column. */
  extraFields: AmountField[];
  /** A client's name, wherever they come from, so the table and the notice name them alike. */
  nameOf: (clientId: string) => string;
  /** The client whose own tab the form was opened from, whose row says so. */
  viewingClientId?: string;
  /** What is wrong with the people's figures taken together, or why a save refused them. */
  error?: string;
  /** Why one person's figure cannot stand, where that depends on more than their own row. */
  fieldError?: (allocation: Allocation, key: string) => string | undefined;
  onAdd: (clientId: string) => void;
  onRemove: (clientId: string) => void;
  onAmountChange: (clientId: string, key: string, value: number | '') => void;
};

export function toAmount(value: string | number): number | '' {
  if (value === '' || value === null) return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
}

/** Null when no person has the amount recorded, so the summary can say so rather than zero. */
export function sumAmounts(allocations: Allocation[], key: string): number | null {
  const recorded = allocations
    .map(allocation => allocation.amounts[key])
    .filter((value): value is number => typeof value === 'number');
  return recorded.length === 0 ? null : recorded.reduce((total, value) => total + value, 0);
}
